import { ICategoryPageMatch } from "@faire/retailer-shared/categories/ICategoryPageMatch";
import { getUrlIdsFromNavigationPath } from "@faire/retailer-shared/categories/navigationPath";
import { ICategory } from "@faire/web-api/indigofair/data/ICategory";
import { route as homepageRoute } from "@faire/web-api/routes/root";
import { IRoutingContext } from "@faire/web/ui/routing/RoutingContext";
import { RouteComponentProps } from "react-router-dom";

import { Category } from "@faire/retailer/models/Category/Category";
import { IUrlId } from "@faire/retailer/models/Category/IUrlId";
import { QueryParameters } from "@faire/retailer/paths/category/QueryParameters";
import { CategoryPagePaths } from "@faire/retailer/paths/CategoryPage";
import { findActiveCategoryByUrlIds } from "@faire/retailer/stores/domain/CategoryStore/findByUrlId";

export const getCategoryRedirect = (
  category: Category,
  {
    match,
    location,
  }: Pick<RouteComponentProps<ICategoryPageMatch>, "match" | "location">,
  categories: ICategory[],
  routingContext: IRoutingContext
): string | undefined => {
  const { brandValue, c1SuppliersUrlId } = match.params;

  if (category === undefined) {
    return homepageRoute;
  }

  const categoryUrlIds = category.urlIdStrings;
  // If the category exists, don't redirect
  if (findActiveCategoryByUrlIds(categoryUrlIds, categories) !== undefined) {
    return undefined;
  }

  const categoryPathParams = {
    queryParams: QueryParameters.parse(location.search),
    routeType: CategoryPagePaths.getRouteType(c1SuppliersUrlId),
    filterByBrandValueOption: brandValue,
  };

  // First check if there is a redirect in the category tree
  const redirectUrlInCategoryTree = findRedirectInCategoryTree(
    categoryUrlIds,
    categories
  );
  if (redirectUrlInCategoryTree !== undefined) {
    return CategoryPagePaths.index({
      categoryUrlIds: redirectUrlInCategoryTree.map((urlId) => ({
        id: urlId,
      })) as [IUrlId, ...IUrlId[]],
      ...categoryPathParams,
      routingContext,
    });
  }

  const validCategoryUrlIds: [IUrlId, ...IUrlId[]] = [category.urlId];
  // Because it is possible to have inactive seasonal categories which return results but do NOT exist in the tree
  // Cannot validate that the root category has a path identifier
  // This logic should be removable once all seasonal categories are migrated to the navigation tree
  let categoryToValidate = category.subCategory;

  // If subcategories don't have path identifiers, they are not in the tree.
  // So redirect to the closest ancestor that exists or the closest C1.
  while (categoryToValidate !== undefined) {
    if (categoryToValidate.pathIdentifier === undefined) {
      return CategoryPagePaths.index({
        categoryUrlIds: validCategoryUrlIds,
        ...categoryPathParams,
        routingContext,
      });
    }
    validCategoryUrlIds.push(categoryToValidate.urlId);
    categoryToValidate = categoryToValidate.subCategory;
  }

  return undefined;
};

interface RedirectMap {
  [key: string]: RedirectNode;
}
interface RedirectNode {
  redirect?: [...string[], string];
  [key: string]: RedirectNode | [...string[], string] | undefined;
}

/**
 * Looks through the category tree to find a redirect for the given category url ids
 * The redirect information for a category is stored in the redirect_navigation_paths
 * which represents the navigation path which redirect into it
 *
 * @param urlIds url ids of the category which we are trying to find a redirect for
 * @param categories category tree to search for redirects
 * @returns
 */
const findRedirectInCategoryTree = (
  urlIds: string[],
  categories: ICategory[]
) => {
  // Build a map of redirects for the current categories in the tree
  // Done to allow for easier lookup algorithm and potential caching of the map
  // if needed for performance
  const redirectMap = {};
  buildRedirectMapForCategories(redirectMap, categories, []);

  let redirectNode: RedirectNode | undefined = redirectMap;
  const categoryUrlIds = urlIds.map((urlId) => urlId.toLowerCase());
  for (const urlId of categoryUrlIds) {
    redirectNode = redirectNode[urlId] as RedirectNode | undefined;
    if (redirectNode === undefined) {
      return undefined;
    }
  }

  return redirectNode?.redirect;
};

/**
 * Build a map of redirects for the given categories and their subcategories.
 * The resulting map is a tree structure where each key represents the category redirect for that category
 * The category that the url id maps to is foudn under the redirect property.
 * All other keys represent the redirect info for the child
 *
 * @param redirectMap Map object to be built. Modified in place
 * @param categories Categories to build the map for
 * @param parentUrlIds The parents url ids of the categories passed in. For example if this is a
 * group of L2 catgories, the parentUrlIds would be the url id of the L1 category
 */
const buildRedirectMapForCategories = (
  redirectMap: RedirectMap,
  categories: ICategory[],
  parentUrlIds: string[]
) => {
  for (const category of categories) {
    if (category.url_id === undefined) {
      continue;
    }

    const currentCategoryNavigationPath = [
      ...parentUrlIds,
      category.url_id,
    ] as [...string[], string];
    for (const redirectNavigationPath of category.redirect_navigation_paths) {
      addRedirectNode(
        redirectMap,
        getUrlIdsFromNavigationPath(redirectNavigationPath),
        currentCategoryNavigationPath
      );
    }
    buildRedirectMapForCategories(
      redirectMap,
      category.sub_categories,
      currentCategoryNavigationPath
    );
  }
};

/**
 * Adds an entry into the redirect map for the given redirect url ids
 *
 * @param redirectMap Map to add the redirect node to. Modified in place
 * @param redirectUrlIds The category url ids that should redirect
 * @param targetUrlIds The category url id the redirectUrlIds should redirect to
 */
const addRedirectNode = (
  redirectMap: RedirectMap,
  redirectUrlIds: string[],
  targetUrlIds: [...string[], string]
) => {
  let currentNode: RedirectNode = redirectMap;
  for (const urlId of redirectUrlIds) {
    const normalizedUrlId = urlId.toLowerCase();
    const node = (currentNode[normalizedUrlId] ?? {}) as RedirectNode;
    if (currentNode[normalizedUrlId] === undefined) {
      currentNode[normalizedUrlId] = node;
    }
    currentNode = node;
  }
  currentNode.redirect = targetUrlIds;
};
